import React, {useEffect, useState} from "react";
import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {useRouter} from "next/router";
import {
  fetchNutritionTarget,
  reset,
  selectError,
  selectIsLoading,
  selectNutritionTarget
} from "../../redux/macroPlan/macroPlanSlice";
import {StoreEmptyStateView} from "../storeFeed/StoreEmptyStateView";
import {ArrowNextExtendedIcon, WarningColorIcon} from "../icons";
import {Heading3, HeroHeading2, Paragraph} from "../text";
import {ApiError} from "../../errors/apiError";
import {RealmServiceError} from "../../errors/realmServiceError";
import {useModalRouting} from "../../utils/useModalRouting";
import {selectUser} from "../../redux/user/userSlice";
import {MacroPlanMacrosView} from "./MacroPlanMacrosView";
import {NutrientType} from "../../models/nutrient";
import Skeleton from "react-loading-skeleton";
import {EventLogger} from "../../utils/eventLogger";
import {AnalyticsEvent} from "../../utils/eventProperties";

export const MacroPlanView: React.FC<any> = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useRouter()
  const { createRoute } = useModalRouting()

  const activatedTrial = router.query.activatedTrial === 'true'
  const nutritionTarget = useAppSelector(selectNutritionTarget)
  const user = useAppSelector(selectUser)
  const isLoading = useAppSelector(selectIsLoading)
  const error = useAppSelector(selectError)

  const [planDetails, setPlanDetails] = useState<string>()

  const [options, setOptions] = useState<{ title: string, id: string, imagePath: string, onClick: () => void }[]>([])

  useEffect(() => {
    EventLogger.logEvent(new AnalyticsEvent({ eventName: 'Screen Visited', screenName: 'Nutrition Plan' }))
  }, [])

  useEffect(() => {
    // Meal delivery ("Get Meals" -> /store-feed) was removed with the deprecated shop.
    setOptions([
      {
        title: t('macro_plan:macro_plan_title_get_a_diet_coach'),
        id: 'diet_coach',
        imagePath: '../images/landing_graphic_diet_coach.webp',
        onClick: () => {
          router.push(activatedTrial ? '/download' : '/upgrade')
        }
      }
    ])
  }, [user])

  useEffect(() => {
    dispatch(reset())
    dispatch(fetchNutritionTarget())
  }, [dispatch])

  useEffect(() => {
    const name = user?.firstName
    const details = nutritionTarget?.parentProtocol?.recommendationFeedback

    if (details) {
      setPlanDetails(t('macro_plan:macro_plan_subtitle_plan_details', { name: name ?? 'there', details }))
    } else {
      setPlanDetails(t('macro_plan:macro_plan_subtitle_week_1_details', { name: name ?? 'there' }))
    }
  }, [user, nutritionTarget, t])

  const isUnauthorizedError = error instanceof ApiError && error.code === 'UNAUTHORIZED' || error instanceof RealmServiceError && error.code === 'NOT_LOGGED_IN'
  const emptyStateButtonTitle = isUnauthorizedError
    ? t('auth:auth_title_sign_in')
    : t('store:store_empty_state_button_try_again')

  return <div className="md:max-w-lg md:rounded-xl md:border-2 md:border-fg-gray-surface md:py-8 px-4 md:px-8 mx-auto mt-10">
    <div className={`${error ? '' : 'hidden'}`}>
      <StoreEmptyStateView
        onClick={() => {
          if (isUnauthorizedError) {
            router.push(
              createRoute({ name: 'login', query: { config: 'sign_in' }, reloadOnClose: true }),
              undefined,
              { shallow: true }
            );
          } else {
            dispatch(reset())
            dispatch(fetchNutritionTarget())
          }
        }}
        buttonTitle={emptyStateButtonTitle}
        title={error?.localizedTitle}
        subtitle={error?.localizedMessage}
        icon={<WarningColorIcon />}
        isLoading={isLoading}
      />
    </div>

    <div className={`${error ? 'hidden' : ''} flex flex-col space-y-4`}>
      <HeroHeading2>
        {isLoading ? <Skeleton className="!w-1/2" /> : t('macro_plan:macro_plan_title_your_nutrition_plan')}
      </HeroHeading2>

      <Paragraph className="text-fg-gray-dark">
        {isLoading ? <Skeleton count={3} className="!w-full" /> : planDetails}
      </Paragraph>

      <MacroPlanMacrosView nutrients={nutritionTarget?.nutrients ?? new Map<NutrientType, number>()} isLoading={isLoading} />

      <HeroHeading2>
        {isLoading ? <Skeleton className="!w-1/3" /> : t('macro_plan:macro_plan_title_whats_next')}
      </HeroHeading2>

      <Paragraph className="text-fg-gray-dark">
        {isLoading ? <Skeleton count={2} className="!w-full" /> : t('macro_plan:macro_plan_subtitle_get_started')}
      </Paragraph>

      <div className={`grid gap-3 ${options.length % 2 === 0 ? 'grid-cols-2': 'grid-cols-1'}`}>
        {
          options.map(option => {
            return <div
              key={option.id}
              className={`rounded-xl border-2 border-fg-gray-surface p-4 flex ${options.length % 2 === 0 ? 'flex-col space-y-3': 'flex-row space-x-3 items-center'} cursor-pointer hover:bg-fg-gray-surface-light transition-colors`}
              onClick={option.onClick}
            >
              <div className={`mx-auto ${isLoading ? 'p-1' : ''}`}>
                { isLoading ?
                  <Skeleton className="!h-16 !w-16 !lg:h-20 !lg:w-20" /> :
                  <img src={option.imagePath}
                       className={`${options.length % 2 === 0 ? 'h-16 w-16 lg:h-20 lg:w-20' : 'h-12 w-12 lg:h-16 lg:w-16'} object-contain mx-auto`}
                  />
                }
              </div>
              <Heading3 className={`h-16 overflow-ellipsis ${options.length % 2 === 0 ? '': 'flex-grow'}`} style={{ maxLines: 2 }}>{isLoading ? <Skeleton count={2} className="!w-full" /> : option.title}</Heading3>

              { isLoading ?
                <Skeleton className="!h-8 !w-8 !rounded-full" /> :
                <div className="cursor-pointer rounded-full h-8 w-8 p-2 flex items-center justify-center bg-fg-primary text-white">
                  <ArrowNextExtendedIcon className="text-white h-4 w-4" />
                </div>
              }
            </div>
          })
        }
      </div>
    </div>
  </div>
}